import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  CheckCircle2,
  Palette,
  Files,
  Globe2,
  Github,
  Twitter,
  MessageCircle,
  Share,
  Sparkles,
  Menu,
  Check,
  Zap,
  Crown,
  Loader2,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { cn } from "../../lib/utils";
import { loadHistory } from "../../app/features/tweets";
import { redirectToCheckout } from "../../services/stripeService";
import { getUserSubscription } from "../../services/subscriptionService";
import { loginWithGoogle } from "../../app/features/auth";
import toast from "react-hot-toast";
import {
  Button,
  Badge,
  Card,
  Navigation,
  HeroSection,
  FeaturesSection,
  Footer,
} from "../../components";

const HomePage = () => {
  const navigate = useNavigate();
  const { token, session, user } = useSelector((state) => state.auth);
  const [tweetUrl, setTweetUrl] = useState("");
  const [openFAQ, setOpenFAQ] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);

  const dispatch = useDispatch();

  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  const loadSubscription = async () => {
    if (!user?.id) {
      return;
    }

    try {
      const { subscription: sub, error } = await getUserSubscription(user.id);
      if (error) {
        console.error("Error loading subscription:", error);
      } else {
        setSubscription(sub);
      }
    } catch (error) {
      console.error("Exception loading subscription:", error);
    }
  };

  useEffect(() => {
    document.documentElement.classList.add("dark");
    // Load export history on mount
    dispatch(loadHistory());
    loadSubscription();
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, [dispatch, user]);

  const handleSubscribe = async (planType) => {
    if (!session || !user) {
      // User needs to sign in first
      toast.error("Please sign in to subscribe");
      try {
        await dispatch(loginWithGoogle()).unwrap();
      } catch (error) {
        console.error("Error signing in:", error);
        toast.error("Failed to sign in. Please try again.");
      }
      return;
    }

    const priceId =
      planType === "plan_b"
        ? import.meta.env.REACT_APP_STRIPE_PLAN_B_PRICE_ID
        : import.meta.env.REACT_APP_STRIPE_PLAN_C_PRICE_ID;

    if (!priceId) {
      toast.error("Stripe configuration error. Please contact support.");
      return;
    }

    setLoading(true);
    try {
      await redirectToCheckout(priceId, user.id);
      // User will be redirected to Stripe, so we don't need to do anything else
    } catch (error) {
      console.error("Error redirecting to checkout:", error);
      toast.error(
        error?.message || "Failed to start checkout. Please try again."
      );
      setLoading(false);
    }
  };

  const keyFeatures = [
    {
      icon: "🧠",
      iconComponent: <Palette className="w-6 h-6 text-pink-500" />,
      title: "AI-Powered Analysis",
      desc: "Advanced AI algorithms analyze facial expressions, features, and composition to provide deep insights.",
      image: "✨",
    },
    {
      icon: "🔒",
      iconComponent: <Files className="w-6 h-6 text-green-500" />,
      title: "Privacy First",
      desc: "Your images are processed securely. We don't store your photos after analysis.",
      image: "🛡️",
    },
    {
      icon: "🎯",
      iconComponent: <Globe2 className="w-6 h-6 text-teal-500" />,
      title: "Accurate Insights",
      desc: "Get precise personality feedback based on advanced psychological and AI analysis models.",
      image: "🤖",
    },
  ];

  return (
    <div className="min-h-screen bg-stone-950 text-zinc-100 font-sans selection:bg-teal-500/30">
      <Navigation />

      <HeroSection tweetUrl={tweetUrl} setTweetUrl={setTweetUrl} />

      <FeaturesSection keyFeatures={keyFeatures} />

      {/* Solutions Showcase - Unique Split Design */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 relative overflow-hidden">
        {/* Unique Background with Diagonal Split */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-rose-950/20 to-transparent"></div>
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-teal-950/20 to-transparent"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-full bg-gradient-to-b from-transparent via-teal-500/30 to-transparent"></div>
        </div>
      </section>

      {/* How it Works Section - Unique Vertical Timeline */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 relative overflow-hidden">
        {/* Unique Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950"></div>
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-rose-500/20 to-transparent"></div>

        <div className="max-w-7xl mx-auto relative">
          {/* Centered Header */}
          <div className="text-center mb-16 sm:mb-20">
            <div className="inline-flex items-center gap-3 mb-6 px-5 py-2.5 rounded-full bg-rose-500/10 border border-rose-500/20">
              <div className="w-2 h-2 rounded-full bg-rose-400 animate-pulse"></div>
              <span className="text-sm font-semibold text-rose-400">
                How It Works
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 leading-tight">
              Comprehensive Analysis
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-teal-400">
                Deep Personality Insights
              </span>
            </h2>
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto">
              The most advanced AI personality analysis tool. Get detailed
              insights about traits, strengths, and characteristics.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Feature List with Unique Design */}
            <div className="space-y-4">
              {[
                {
                  icon: <Menu className="w-5 h-5" />,
                  title: "Upload Image",
                  desc: "Upload your photo in any format - JPG, PNG, or HEIC.",
                  num: "01",
                },
                {
                  icon: (
                    <span className="text-xl font-bold text-teal-500">🤖</span>
                  ),
                  title: "AI Analysis",
                  desc: "Advanced AI analyzes facial features and expressions.",
                  num: "02",
                },
                {
                  icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
                  title: "Personality Traits",
                  desc: "Get detailed insights on confidence, openness, creativity, and more.",
                  num: "03",
                },
                {
                  icon: <Files className="w-5 h-5" />,
                  title: "Detailed Report",
                  desc: "Receive comprehensive personality feedback and recommendations.",
                  num: "04",
                },
                {
                  icon: <Share className="w-5 h-5" />,
                  title: "Export & Share",
                  desc: "Download your personality report or share insights.",
                  num: "05",
                },
              ].map((feature, index) => (
                <div key={index} className="relative group">
                  {/* Number Badge */}
                  <div className="absolute -left-4 top-0 w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-teal-500 flex items-center justify-center text-white text-xs font-bold border-2 border-stone-900 z-10">
                    {feature.num}
                  </div>

                  <div className="ml-6 p-5 rounded-xl border-2 border-stone-800/50 bg-stone-900/30 hover:border-rose-500/50 hover:bg-stone-900/50 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-stone-800/50 border border-stone-700/50 group-hover:border-rose-500/50 group-hover:bg-rose-500/10 transition-all">
                        <div className="text-rose-400 group-hover:text-rose-300 transition-colors">
                          {feature.icon}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1 group-hover:text-rose-400 transition-colors">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-zinc-400 leading-relaxed">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="relative py-20 sm:py-28 px-4 sm:px-6 overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 via-rose-500/10 to-orange-500/10 border border-teal-500/20 mb-6">
              <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
              <span className="text-xs sm:text-sm font-medium text-teal-400">
                Choose Your Plan
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-zinc-100 via-teal-100 to-rose-100 bg-clip-text text-transparent">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto">
              Start with our free tier, then upgrade when you need more analyses
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              {
                name: "Free",
                planType: "free",
                price: "$0",
                period: "forever",
                description: "Perfect for trying out our service",
                features: [
                  "1 analysis per 24 hours",
                  "Basic personality insights",
                  "Posture analysis",
                  "Style recommendations",
                  "No credit card required",
                ],
                cta: "Current Plan",
                popular: false,
                icon: <Zap className="w-6 h-6" />,
              },
              {
                name: "Basic",
                planType: "plan_b",
                price: "$4",
                period: "per month",
                description: "Great for regular users",
                features: [
                  "5 analyses per 24 hours",
                  "All free features",
                  "Priority processing",
                  "Email support",
                  "Cancel anytime",
                ],
                cta:
                  subscription?.plan_type === "plan_b"
                    ? "Current Plan"
                    : "Subscribe",
                popular: true,
                icon: <Crown className="w-6 h-6" />,
              },
              {
                name: "Pro",
                planType: "plan_c",
                price: "$10",
                period: "per month",
                description: "For power users and professionals",
                features: [
                  "Unlimited analyses",
                  "All Basic features",
                  "Fastest processing",
                  "Priority support",
                  "Advanced insights",
                  "Cancel anytime",
                ],
                cta:
                  subscription?.plan_type === "plan_c"
                    ? "Current Plan"
                    : "Subscribe",
                popular: false,
                icon: <Crown className="w-6 h-6" />,
              },
            ].map((plan) => {
              const currentPlan = subscription?.plan_type || "free";
              const isCurrentPlan = currentPlan === plan.planType;
              const isPopular = plan.popular;

              return (
                <div
                  key={plan.planType}
                  className={`relative rounded-2xl p-6 sm:p-8 border transition-all duration-300 ${
                    isPopular
                      ? "border-teal-500/50 bg-gradient-to-br from-teal-950/30 to-stone-900/50 scale-105"
                      : isCurrentPlan
                      ? "border-rose-500/50 bg-gradient-to-br from-rose-950/20 to-stone-900/50"
                      : "border-zinc-800 bg-stone-900/30 hover:border-zinc-700"
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-teal-500 text-white text-xs font-semibold">
                      Most Popular
                    </div>
                  )}

                  {isCurrentPlan && (
                    <div className="absolute -top-4 right-4 px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-semibold">
                      Current
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`p-2 rounded-lg ${
                          isPopular
                            ? "bg-teal-500/20 text-teal-400"
                            : "bg-rose-500/20 text-rose-400"
                        }`}
                      >
                        {plan.icon}
                      </div>
                      <h3 className="text-2xl font-bold">{plan.name}</h3>
                    </div>
                    <p className="text-zinc-400 text-sm mb-4">
                      {plan.description}
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-zinc-400">{plan.period}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleSubscribe(plan.planType)}
                    disabled={isCurrentPlan || loading}
                    className={`w-full ${
                      isCurrentPlan
                        ? "bg-zinc-800 text-zinc-400 cursor-not-allowed"
                        : isPopular
                        ? "bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                        : "bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700"
                    }`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <p className="text-zinc-400 text-sm">
              All plans include our full analysis features. Upgrade or cancel
              anytime.
            </p>
            {subscription && subscription.plan_type !== "free" && (
              <p className="text-zinc-400 text-sm mt-2">
                Your current plan:{" "}
                <span className="text-teal-400 font-semibold">
                  {subscription.plan_type === "plan_b" ? "Basic" : "Pro"}
                </span>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        className="py-24 px-4 sm:px-6 border-t border-stone-900 relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-rose-600/10 rounded-full blur-[120px] -z-10"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-teal-600/10 rounded-full blur-[100px] -z-10"></div>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 mb-4">
              FAQs
            </Badge>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 break-words px-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto break-words px-4">
              Everything you need to know about our AI personality analysis
              platform. Can't find the answer you're looking for? Contact our
              support team.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "How do I get personality feedback?",
                answer:
                  "Simply upload your image using our upload button and click 'Analyze Now'. Our AI will instantly analyze your photo and provide detailed personality feedback. No sign-up required for basic use.",
              },
              {
                question: "Is the service free to use?",
                answer:
                  "Yes! We offer a free tier that allows you to get basic personality analysis. For advanced features like detailed reports and bulk processing, we offer paid plans starting at $4/month.",
              },
              {
                question: "How accurate is the AI analysis?",
                answer:
                  "Our AI uses advanced algorithms trained on psychological models to provide accurate personality insights. While results are based on facial analysis, they should be used as guidance rather than definitive assessments.",
              },
              {
                question: "What image formats are supported?",
                answer:
                  "We support all common image formats including JPG, PNG, HEIC, and WebP. Upload any photo format and our AI will analyze it to provide personality feedback.",
              },
              {
                question: "How detailed are the personality reports?",
                answer:
                  "Our reports include detailed analysis of personality traits like confidence, openness, creativity, and more. Paid plans include comprehensive reports with recommendations and insights.",
              },
              {
                question: "Is my data secure?",
                answer:
                  "Yes, privacy is our priority. We don't store your images after analysis. All processing happens in real-time, and we don't track your usage. Your data stays private and secure.",
              },
              {
                question: "Can I export my personality report?",
                answer:
                  "You can download your personality report in PDF format.",
              },
              {
                question: "Do you offer bulk image analysis?",
                answer:
                  "Yes, bulk processing is available on Pro+ plan. You can upload unlimited images at once and receive comprehensive analysis reports. Perfect for researchers and professionals.",
              },
            ].map((faq, index) => (
              <Card
                key={index}
                className="overflow-hidden transition-all hover:border-stone-700"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full p-4 sm:p-6 text-left flex items-center justify-between group"
                >
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-white group-hover:text-rose-400 transition-colors pr-6 sm:pr-8 break-words flex-1 min-w-0">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    <ChevronDown
                      className={cn(
                        "w-5 h-5 text-zinc-400 transition-transform duration-300",
                        openFAQ === index && "transform rotate-180"
                      )}
                    />
                  </div>
                </button>
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-300",
                    openFAQ === index
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  )}
                >
                  <div className="px-4 sm:px-6 pb-6">
                    <p className="text-sm sm:text-base text-zinc-400 leading-relaxed break-words">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-zinc-400 mb-4">Still have questions?</p>
            <Button
              variant="outline"
              className="hover:bg-stone-800 hover:border-stone-700"
              onClick={() => {
                if (window.Tawk_API && window.Tawk_API.maximize) {
                  window.Tawk_API.maximize();
                }
              }}
            >
              Contact Support
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
